/**
 * TransportMate Observability (Phase 12)
 * ======================================
 * Additive, fire-and-forget telemetry. NEVER throws, never blocks a
 * workflow, never changes behaviour. If ingestion fails the app carries on.
 *
 * Captures:
 *   • errors (frontend exceptions, render errors, API/edge failures,
 *     automation/sync/native/upload failures, unhandled rejections)
 *   • performance metrics (page load, API latency, uploads, image loads)
 *
 * Redaction rules (hard requirements):
 *   • never send passwords, tokens, secrets, payment details or raw
 *     customer PII — values are scrubbed before leaving the device.
 */

import { getDeviceDescriptor, getReleaseInfo, getRuntimeContext } from "@/lib/releaseInfo";
import { addBreadcrumb, getBreadcrumbs } from "@/lib/breadcrumbs";

export type ObservabilityKind = "error" | "performance" | "native";
export type ObservabilitySeverity = "debug" | "info" | "warn" | "error" | "fatal";

export interface ObservabilityEvent {
  kind: ObservabilityKind;
  severity?: ObservabilitySeverity;
  component: string;
  message?: string;
  stack?: string | null;
  route?: string;
  duration_ms?: number;
  metric_name?: string;
  metric_value?: number;
  metadata?: Record<string, unknown>;
  /**
   * "high" events bypass the ordinary per-minute cap (they use a separate,
   * larger budget). Reserved for persistence/diagnostic trails that must be
   * complete enough to identify a first failing stage.
   */
  priority?: "normal" | "high";
}

interface Context {
  userId: string | null;
  companyId: string | null;
  userRole: string | null;
}

const MAX_BUFFER = 40;
const FLUSH_INTERVAL_MS = 15_000;
const MAX_EVENTS_PER_MINUTE = 60;
const MAX_HIGH_PRIORITY_PER_MINUTE = 300;
const SESSION_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let correlationId = newCorrelationId();
let context: Context = { userId: null, companyId: null, userRole: null };
let buffer: Record<string, unknown>[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let sentThisMinute = 0;
let highPriorityThisMinute = 0;
let minuteStart = Date.now();
let initialised = false;

function newCorrelationId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Rotate the correlation id (e.g. per navigation / workflow). */
export function newCorrelation(): string {
  correlationId = newCorrelationId();
  return correlationId;
}

export function getCorrelationId(): string {
  return correlationId;
}

export function getObservabilitySessionId(): string {
  return SESSION_ID;
}

/** Set the current identity context. Ids only — never names or emails. */
export function setObservabilityContext(next: Partial<Context>) {
  context = { ...context, ...next };
}

// ── Redaction ────────────────────────────────────────────────────
const SENSITIVE_KEY = /(pass|pwd|token|secret|key|auth|bearer|cookie|session|card|cvv|iban|sort_?code|account_?number|signature|email|phone|postcode|address)/i;
const SENSITIVE_VALUE_PATTERNS: RegExp[] = [
  /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g, // JWT
  /sk_(live|test)_[A-Za-z0-9]+/g, // Stripe secret keys
  /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g, // emails
  /\b(?:\d[ -]*?){13,19}\b/g, // card-like numbers
  /Bearer\s+[A-Za-z0-9._-]+/gi,
];

export function redactString(input: string): string {
  let out = input;
  for (const re of SENSITIVE_VALUE_PATTERNS) out = out.replace(re, "[redacted]");
  return out;
}

function redactValue(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (typeof value === "string") return redactString(value).slice(0, 500);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= 3) return "[truncated]";
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => redactValue(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>).slice(0, 30)) {
      out[k] = SENSITIVE_KEY.test(k) ? "[redacted]" : redactValue(v, depth + 1);
    }
    return out;
  }
  return undefined;
}

// ── Buffering & transport ────────────────────────────────────────
function withinRateLimit(highPriority = false): boolean {
  const now = Date.now();
  if (now - minuteStart > 60_000) {
    minuteStart = now;
    sentThisMinute = 0;
    highPriorityThisMinute = 0;
  }
  if (highPriority) {
    if (highPriorityThisMinute >= MAX_HIGH_PRIORITY_PER_MINUTE) return false;
    highPriorityThisMinute += 1;
    return true;
  }
  if (sentThisMinute >= MAX_EVENTS_PER_MINUTE) return false;
  sentThisMinute += 1;
  return true;
}

/** Record an observability event. Never throws. */
export function captureEvent(event: ObservabilityEvent): void {
  try {
    if (!withinRateLimit(event.priority === "high")) return;
    const release = getReleaseInfo();
    buffer.push({
      occurred_at: new Date().toISOString(),
      kind: event.kind,
      severity: event.severity || (event.kind === "error" ? "error" : "info"),
      component: event.component?.slice(0, 120) || "unknown",
      route: (event.route ?? window.location?.pathname ?? "").slice(0, 200),
      message: event.message ? redactString(String(event.message)).slice(0, 1000) : null,
      stack: event.stack ? redactString(String(event.stack)).slice(0, 4000) : null,
      correlation_id: correlationId,
      session_id: SESSION_ID,
      environment: release.environment,
      release: release.release,
      app_version: release.appVersion,
      build_number: release.buildNumber,
      git_commit: release.gitCommit,
      platform: release.platform,
      device: getDeviceDescriptor(),
      user_role: context.userRole,
      user_id: context.userId,
      company_id: context.companyId,
      duration_ms:
        typeof event.duration_ms === "number" && Number.isFinite(event.duration_ms)
          ? Math.round(event.duration_ms)
          : null,
      metric_name: event.metric_name || null,
      metric_value:
        typeof event.metric_value === "number" && Number.isFinite(event.metric_value)
          ? event.metric_value
          : null,
      metadata: redactValue({
        ...(event.metadata || {}),
        runtime: getRuntimeContext(),
        ...(event.kind === "error" ? { breadcrumbs: getBreadcrumbs().slice(-15) } : {}),
      }) as Record<string, unknown>,
    });

    // Errors and native events also become breadcrumbs for later crashes.
    if (event.kind === "error") {
      addBreadcrumb("system", `error:${event.component}`, {
        severity: event.severity || "error",
      });
    }

    if (buffer.length >= MAX_BUFFER) void flushObservability();
  } catch {
    /* observability must never break the app */
  }
}

/**
 * Transient, self-recovering client conditions. They remain recorded as errors
 * but at warn severity, so an expected network drop or an IndexedDB
 * transaction that closed between ticks cannot raise a CRITICAL incident.
 */
const TRANSIENT_ERROR_PATTERNS = [
  /without an in-progress transaction/i,
  /transaction (?:is )?(?:inactive|finished|aborted)/i,
  /(?:network|load failed|failed to fetch|networkerror)/i,
  /the operation was aborted/i,
];

export function isTransientClientError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return TRANSIENT_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * External automation/extension bridge noise. The exact string
 * "Object Not Found Matching Id:N, MethodName:x, ParamCount:n" is never
 * produced by TransportMate code; production telemetry shows it only as
 * plain-string unhandled rejections in millisecond bursts from
 * unauthenticated desktop sessions (scanner/extension injection).
 * Filtered ONLY when the rejection reason is a bare string — a genuine
 * Error carrying a stack with the same text is still captured.
 */
const EXTERNAL_BRIDGE_REJECTION =
  /^Object Not Found Matching Id:\d+, MethodName:[A-Za-z]+, ParamCount:\d+$/;

export function isExternalBridgeRejection(reason: unknown): boolean {
  return typeof reason === "string" && EXTERNAL_BRIDGE_REJECTION.test(reason.trim());
}

/**
 * Diagnostic-only description of a promise rejection reason. Never includes
 * the reason payload itself; only its type, original name/stack presence and a
 * redacted, truncated user agent. Message/grouping are untouched.
 */
export function describeRejectionReason(reason: unknown): Record<string, unknown> {
  const r = reason as { name?: unknown; stack?: unknown; constructor?: { name?: string } } | null | undefined;
  const isObj = r !== null && typeof r === "object";
  let userAgent: string | null = null;
  try {
    userAgent = typeof navigator !== "undefined" && navigator.userAgent
      ? redactString(navigator.userAgent).slice(0, 300)
      : null;
  } catch { /* ignore */ }
  return {
    reason_type: reason === null ? "null" : Array.isArray(reason) ? "array" : typeof reason,
    reason_is_error: reason instanceof Error,
    reason_constructor: isObj ? String(r?.constructor?.name || "Object").slice(0, 60) : null,
    original_error_name: isObj && typeof r?.name === "string" ? r.name.slice(0, 120) : null,
    original_stack_available: isObj && typeof r?.stack === "string" && r.stack.length > 0,
    user_agent: userAgent,
  };
}

/** Convenience helpers. */
export function captureError(
  component: string,
  error: unknown,
  extra?: { route?: string; severity?: ObservabilitySeverity; metadata?: Record<string, unknown> },
) {
  const err = error instanceof Error ? error : new Error(String(error));
  // Preserve an original stack carried by non-Error rejection objects
  // (e.g. DOMException-like or plain {message, stack}) instead of the
  // synthetic wrapper stack.
  const originalStack =
    !(error instanceof Error) && error && typeof error === "object" &&
    typeof (error as { stack?: unknown }).stack === "string"
      ? ((error as { stack: string }).stack)
      : null;
  captureEvent({
    kind: "error",
    severity: extra?.severity || (isTransientClientError(err) ? "warn" : "error"),
    component,
    message: err.message,
    stack: originalStack || err.stack || null,
    route: extra?.route,
    metadata: extra?.metadata,
  });
}


export function captureMetric(
  component: string,
  metricName: string,
  value: number,
  metadata?: Record<string, unknown>,
) {
  captureEvent({
    kind: "performance",
    severity: "info",
    component,
    metric_name: metricName,
    metric_value: value,
    duration_ms: value,
    metadata,
  });
}

/** Time an async operation and record its duration (result is passed through). */
export async function measure<T>(
  component: string,
  metricName: string,
  fn: () => Promise<T>,
  metadata?: Record<string, unknown>,
): Promise<T> {
  const started = performance.now();
  try {
    const result = await fn();
    captureMetric(component, metricName, performance.now() - started, metadata);
    return result;
  } catch (error) {
    captureMetric(component, metricName, performance.now() - started, { ...metadata, failed: true });
    captureError(component, error, { metadata: { ...metadata, metric: metricName } });
    throw error;
  }
}

const OFFLINE_KEY = "tm_observability_offline_v1";
const MAX_OFFLINE_EVENTS = 200;

/** Persist undeliverable events so diagnostics survive no-signal working days. */
function persistOffline(events: Record<string, unknown>[]): void {
  try {
    const existing = readOffline();
    const merged = [...existing, ...events].slice(-MAX_OFFLINE_EVENTS);
    localStorage.setItem(OFFLINE_KEY, JSON.stringify(merged));
  } catch {
    /* storage full / unavailable — drop silently */
  }
}

function readOffline(): Record<string, unknown>[] {
  try {
    const raw = localStorage.getItem(OFFLINE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function clearOffline(): void {
  try {
    localStorage.removeItem(OFFLINE_KEY);
  } catch {
    /* ignore */
  }
}

/** Number of diagnostics waiting for connectivity (diagnostics only). */
export function getStoredDiagnosticsCount(): number {
  return readOffline().length;
}

async function postEvents(events: Record<string, unknown>[], useBeacon: boolean): Promise<boolean> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ingest-observability`;
  const payload = JSON.stringify({ events });

  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    return navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
  }

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: payload,
    keepalive: true,
  });
  return res.ok;
}

export async function flushObservability(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];

  try {
    const ok = await postEvents(events, useBeacon);
    if (!ok) persistOffline(events);
  } catch {
    persistOffline(events);
  }
}

/** Re-send diagnostics captured while the device had no connectivity. */
export async function flushStoredDiagnostics(): Promise<void> {
  try {
    const stored = readOffline();
    if (stored.length === 0) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    clearOffline();
    const ok = await postEvents(stored, false).catch(() => false);
    if (!ok) persistOffline(stored);
  } catch {
    /* never throw */
  }
}

// ── Global capture (installed once from main.tsx) ────────────────
export function initObservability(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;

  try {
    window.addEventListener("error", (event) => {
      captureError("window.onerror", event.error || event.message, {
        metadata: { source: event.filename, line: event.lineno },
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      const reason = (event as PromiseRejectionEvent).reason;
      // Drop known external automation/extension bridge noise (bare-string
      // rejections only); genuine app rejections are still captured.
      if (isExternalBridgeRejection(reason)) return;
      captureError("unhandledrejection", reason, {
        severity: "error",
        metadata: describeRejectionReason(reason),
      });
    });

    window.addEventListener("pagehide", () => void flushObservability(true));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flushObservability(true);
    });

    window.addEventListener("online", () => {
      addBreadcrumb("network", "back_online");
      void flushObservability();
      void flushStoredDiagnostics();
    });
    window.addEventListener("offline", () => addBreadcrumb("network", "offline"));

    timer = setInterval(() => {
      void flushObservability();
      void flushStoredDiagnostics();
    }, FLUSH_INTERVAL_MS);

    void flushStoredDiagnostics();

    // Page load performance (one sample per boot).
    window.addEventListener("load", () => {
      setTimeout(() => {
        try {
          const nav = performance.getEntriesByType("navigation")[0] as
            | PerformanceNavigationTiming
            | undefined;
          if (!nav) return;
          captureMetric("app.launch", "app_launch_ms", Math.round(nav.loadEventEnd || nav.duration), {
            ttfb: Math.round(nav.responseStart),
          });
          captureMetric("app.page_load", "page_load_ms", nav.duration, {
            dom_content_loaded: Math.round(nav.domContentLoadedEventEnd),
            ttfb: Math.round(nav.responseStart),
            type: nav.type,
          });
        } catch {
          /* ignore */
        }
      }, 0);
    });
  } catch {
    /* ignore */
  }
}

export function stopObservability(): void {
  if (timer) clearInterval(timer);
  timer = null;
  initialised = false;
}
